/**
 * The History drawer on Settings › Credit: every change to the billing
 * account, newest first — what changed, before → after, who, when and why. A
 * change an import made shows the import's label instead of a reason
 * (docs/features/credit-control/06_ui_strategy.md §2). The newest page shows
 * first; "Show more" reads the next one.
 */
import { useEffect, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { BillingAccountChange } from "@shared-core/data/credit-control-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../primitives/sheet";
import { Skeleton } from "../../primitives/skeleton";

const PAGE_SIZE = 25;

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | {
      kind: "loaded";
      changes: BillingAccountChange[];
      total: number;
      page: number;
      loadingMore: boolean;
      moreError: string | null;
    };

function inWords(err: unknown): string {
  return err instanceof Error ? err.message : "Could not load the history.";
}

export function CreditHistoryDrawer({
  accountRef,
  accountName,
  open,
  onOpenChange,
}: {
  accountRef: string;
  accountName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Each (re)open starts a new read; a page that arrives for an earlier one is dropped.
  const activeRead = useRef<{ cancelled: boolean } | null>(null);

  useEffect(() => {
    if (!open) return;
    const read = { cancelled: false };
    activeRead.current = read;
    setState({ kind: "loading" });
    adapter
      .listBillingAccountChanges(accountRef, { page: 1, pageSize: PAGE_SIZE })
      .then((first) => {
        if (read.cancelled) return;
        setState({
          kind: "loaded",
          changes: first.items,
          total: first.total,
          page: first.page,
          loadingMore: false,
          moreError: null,
        });
      })
      .catch((err: unknown) => {
        if (read.cancelled) return;
        setState({ kind: "failed", message: inWords(err) });
      });
    return () => {
      read.cancelled = true;
    };
  }, [adapter, accountRef, open]);

  const showMore = () => {
    const read = activeRead.current;
    if (!read || state.kind !== "loaded" || state.loadingMore) return;
    const nextPage = state.page + 1;
    setState({ ...state, loadingMore: true, moreError: null });
    adapter
      .listBillingAccountChanges(accountRef, {
        page: nextPage,
        pageSize: PAGE_SIZE,
      })
      .then((next) => {
        if (read.cancelled) return;
        setState((prev) =>
          prev.kind === "loaded"
            ? {
                ...prev,
                changes: [...prev.changes, ...next.items],
                total: next.total,
                page: next.page,
                loadingMore: false,
              }
            : prev,
        );
      })
      .catch((err: unknown) => {
        if (read.cancelled) return;
        setState((prev) =>
          prev.kind === "loaded"
            ? { ...prev, loadingMore: false, moreError: inWords(err) }
            : prev,
        );
      });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Credit history</SheetTitle>
          <SheetDescription>
            Every change to {accountName}'s billing account ({accountRef}).
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4 pb-4 text-sm">
          {state.kind === "loading" && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}
          {state.kind === "failed" && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{state.message}</AlertDescription>
            </Alert>
          )}
          {state.kind === "loaded" && state.changes.length === 0 && (
            <p className="text-muted-foreground">No changes recorded.</p>
          )}
          {state.kind === "loaded" && state.changes.length > 0 && (
            <ol className="space-y-4">
              {state.changes.map((change) => (
                <li
                  key={change.id}
                  className="space-y-1 border-b border-border pb-3 last:border-0"
                >
                  <p className="font-medium">{change.fieldLabel}</p>
                  <p>
                    <span className="text-muted-foreground">
                      {change.beforeLabel ?? "None"}
                    </span>
                    {" → "}
                    {change.afterLabel ?? "None"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {change.importLabel
                      ? `Imported${change.byName ? ` by ${change.byName}` : ""}, “${change.importLabel}”`
                      : `${change.byName ? `${change.byName}: ` : ""}“${change.reason}”`}
                    {" · "}
                    {formatDateTimeOrDash(change.at)}
                  </p>
                </li>
              ))}
            </ol>
          )}
          {state.kind === "loaded" && state.moreError && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{state.moreError}</AlertDescription>
            </Alert>
          )}
          {state.kind === "loaded" && state.changes.length < state.total && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Showing {state.changes.length} of {state.total}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={showMore}
                disabled={state.loadingMore}
              >
                {state.loadingMore ? "Loading…" : "Show more"}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
